// Polymarket (Gamma) — implied odds, never counts. Reads an event slug (an
// event groups the per-candidate "Will X win?" binary markets) or per-candidate
// market slugs from config. Blank config → "not configured" (no invented odds).
import type { MarketProvider } from '../../types/providers.ts';
import { ProviderError } from '../../types/providers.ts';
import type { MarketOutcome, MarketResult } from '../../types/markets.ts';
import type { CandidateConfig, RaceConfig } from '../../types/config.ts';
import { fetchJson } from '../http.ts';
import { nowIso } from '../../lib/time.ts';

interface GammaMarket {
  question?: string;
  slug?: string;
  outcomes?: string; // JSON-string array, e.g. "[\"Yes\",\"No\"]"
  outcomePrices?: string; // JSON-string array of 0..1 strings, parallel to outcomes
  volume?: string | number;
  volumeNum?: number;
  bestBid?: number;
  bestAsk?: number;
  lastTradePrice?: number;
  closed?: boolean;
  active?: boolean;
}
interface GammaEvent {
  title?: string;
  slug?: string;
  closed?: boolean;
  volume?: number;
  markets?: GammaMarket[];
}

function parseArr(s: string | undefined): string[] {
  if (!s) return [];
  try {
    const a: unknown = JSON.parse(s);
    return Array.isArray(a) ? a.map(String) : [];
  } catch {
    return [];
  }
}

function yesProbability(m: GammaMarket): number | null {
  const outs = parseArr(m.outcomes);
  const prices = parseArr(m.outcomePrices);
  const yesIdx = outs.findIndex((o) => /^yes$/i.test(o));
  const idx = yesIdx >= 0 ? yesIdx : 0;
  const raw = prices[idx];
  const p = raw != null ? Number(raw) : m.lastTradePrice != null ? m.lastTradePrice : null;
  return p != null && Number.isFinite(p) ? Math.max(0, Math.min(1, p)) : null;
}

function matchCandidate(m: GammaMarket, race: RaceConfig): CandidateConfig | null {
  const bySlug = race.candidates.find((c) => c.polymarketMarketSlug && c.polymarketMarketSlug === m.slug);
  if (bySlug) return bySlug;
  const q = (m.question ?? '').toLowerCase();
  return race.candidates.find((c) => q.includes(c.name.toLowerCase())) ?? null;
}

function toOutcome(m: GammaMarket, cand: CandidateConfig | null): MarketOutcome {
  return {
    candidateId: cand?.id ?? null,
    label: cand?.name ?? m.question ?? m.slug ?? 'Outcome',
    probability: yesProbability(m),
    bestBid: m.bestBid ?? null,
    bestAsk: m.bestAsk ?? null,
    volume: typeof m.volumeNum === 'number' ? m.volumeNum : m.volume != null ? Number(m.volume) : null,
  };
}

/** Prefer the race's tracked candidates, sorted by probability; fall back to all
 *  outcomes if none matched, so a name mismatch never blanks a live market. Keeps
 *  the panel to the tracked field instead of every placeholder market. */
function pickOutcomes(all: MarketOutcome[]): MarketOutcome[] {
  const mapped = all.filter((o) => o.candidateId != null);
  return (mapped.length ? mapped : all).sort((x, y) => (y.probability ?? -1) - (x.probability ?? -1));
}

export const polymarketProvider: MarketProvider = {
  id: 'polymarket',
  kind: 'markets',
  async getMarket(race, cfg, signal) {
    const transport = cfg.transport ?? 'auto';
    const perCandidate = race.candidates
      .filter((c) => c.polymarketMarketSlug)
      .map((c) => c.polymarketMarketSlug as string);

    if (cfg.eventSlug) {
      const events = await fetchJson<GammaEvent[]>({
        providerKey: 'polymarket',
        upstreamPath: `events?closed=false&slug=${encodeURIComponent(cfg.eventSlug)}`,
        transport,
        signal,
      });
      const ev = Array.isArray(events) ? events[0] : undefined;
      if (!ev || !ev.markets?.length) {
        throw new ProviderError('not_configured', `No Polymarket event "${cfg.eventSlug}"`);
      }
      return {
        source: 'Polymarket',
        sourceId: 'polymarket',
        fetchedAt: nowIso(),
        eventLabel: ev.title,
        outcomes: pickOutcomes(ev.markets.map((m) => toOutcome(m, matchCandidate(m, race)))),
        totalVolume: ev.volume ?? null,
        closed: ev.closed ?? false,
      } satisfies MarketResult;
    }

    if (perCandidate.length) {
      const qs = perCandidate.map((s) => `slug=${encodeURIComponent(s)}`).join('&');
      const markets = await fetchJson<GammaMarket[]>({
        providerKey: 'polymarket',
        upstreamPath: `markets?closed=false&${qs}`,
        transport,
        signal,
      });
      if (!Array.isArray(markets) || !markets.length) {
        throw new ProviderError('not_configured', 'No Polymarket markets for the configured slugs');
      }
      return {
        source: 'Polymarket',
        sourceId: 'polymarket',
        fetchedAt: nowIso(),
        outcomes: pickOutcomes(markets.map((m) => toOutcome(m, matchCandidate(m, race)))),
        closed: markets.every((m) => m.closed ?? false),
      } satisfies MarketResult;
    }

    throw new ProviderError('not_configured', 'No Polymarket event slug or candidate slugs configured');
  },
};
